import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import moment from 'moment'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import API from '../../../services/API'
import eventTypes from '../../clients/eventTypes'
import EventAddressFields from '../components/EventAddressFields'
import { eventAddressPayload } from '../../../helpers/eventAddress'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import { useNavigate } from 'react-router-dom'
import { EventMetaContext, ShiftContext } from './SingleEvent'

const toIsoOrNull = (d) => {
  if (!d) return null
  if (moment.isMoment(d)) return d.clone().toISOString()

  const iso = moment(d, moment.ISO_8601, true)
  if (iso.isValid()) return iso.toISOString()

  // Support any legacy formats you *actually* store
  const legacy = moment(d, ['DD/MM/YYYY HH:mm', 'DD/MM/YYYY HH:mm:ss'], true)
  if (legacy.isValid()) return legacy.toISOString()

  // Last resort (avoid if possible)
  const loose = moment(d)
  return loose.isValid() ? loose.toISOString() : null
}

const DuplicateEvent = ({ onClose }) => {
  const { orgConfig } = useContext(TenantSettingsContext)
  const { setToast } = useContext(GlobalContext)
  const { eventMeta } = useContext(EventMetaContext)
  const { shifts } = useContext(ShiftContext)
  const navigate = useNavigate()
  const formRef = useRef(null)

  const [step, setStep] = useState(0)

  const [eventName, setEventName] = useState(eventMeta?.title ?? '')

  // Normalize to ISO *once* on init
  const [eventStartDate, setEventStartDate] = useState(() =>
    toIsoOrNull(eventMeta?.startDate),
  )
  const [eventEndDate, setEventEndDate] = useState(() =>
    toIsoOrNull(eventMeta?.endDate),
  )

  const [organisation, setOrganisation] = useState(
    eventMeta?.organisation?._id
      ? {
          _id: eventMeta.organisation._id,
          orgName: eventMeta.organisation.orgName,
        }
      : null,
  )

  const [eventType, setEventType] = useState(eventMeta?.eventType ?? '')
  const [eventAddress, setEventAddress] = useState(
    eventMeta?.eventAddress ?? null,
  )
  const [organisationsOptions, setOrganisationsOptions] = useState([])

  // Clone + normalize shift dates to ISO
  const [shiftsList, setShiftsList] = useState(() =>
    (shifts ?? []).map((s) => ({
      ...s,
      start: toIsoOrNull(s.start),
      end: toIsoOrNull(s.end),
    })),
  )

  const [selectedShifts, setSelectedShifts] = useState(() =>
    (shifts ?? []).map((s) => s._id),
  )

  useEffect(() => {
    API.get('/event-org-options').then((res) =>
      setOrganisationsOptions(res.data),
    )
  }, [])

  const dateRangeInvalid = useMemo(() => {
    if (!eventStartDate || !eventEndDate) return false
    return (
      moment(eventStartDate).isAfter(eventEndDate) ||
      moment(eventEndDate).isBefore(eventStartDate)
    )
  }, [eventStartDate, eventEndDate])

  const updateShift = (shiftId, patch) => {
    setShiftsList((prev) =>
      prev.map((x) => (x._id === shiftId ? { ...x, ...patch } : x)),
    )
  }

  // Original (pre-edit) dates, used as the rebasing anchor so re-entering
  // the shifts step never compounds the offset
  const originalShiftDates = useMemo(() => {
    const byId = {}
    ;(shifts ?? []).forEach((s) => {
      byId[s._id] = { start: toIsoOrNull(s.start), end: toIsoOrNull(s.end) }
    })
    return byId
  }, [shifts])

  const isShiftOutOfRange = (start, rangeStart, rangeEnd) =>
    !!start &&
    (moment(start).isBefore(rangeStart) || moment(start).isAfter(rangeEnd))

  const outOfRangeShiftIds = useMemo(() => {
    if (!eventStartDate || !eventEndDate || dateRangeInvalid) return []
    const rangeStart = moment(eventStartDate).startOf('day')
    const rangeEnd = moment(eventEndDate).endOf('day')
    return shiftsList
      .filter((s) => isShiftOutOfRange(s.start, rangeStart, rangeEnd))
      .map((s) => s._id)
  }, [shiftsList, eventStartDate, eventEndDate, dateRangeInvalid])

  // Anchor on the earliest original shift and move every shift by the same
  // number of days, so the first shift lands on the new event start day and
  // each shift keeps its time of day, duration and relative spacing
  const rebaseShiftsToNewDates = () => {
    const anchor = Object.values(originalShiftDates)
      .map((d) => d.start)
      .filter(Boolean)
      .sort()[0]

    if (!anchor || !eventStartDate || !eventEndDate) return

    const dayOffset = moment(eventStartDate)
      .startOf('day')
      .diff(moment(anchor).startOf('day'), 'days')

    const rangeStart = moment(eventStartDate).startOf('day')
    const rangeEnd = moment(eventEndDate).endOf('day')

    const moved = shiftsList.map((x) => {
      const original = originalShiftDates[x._id]
      if (!original?.start || !original?.end) {
        return { ...x, start: eventStartDate, end: eventEndDate }
      }
      return {
        ...x,
        start: moment(original.start).add(dayOffset, 'days').toISOString(),
        end: moment(original.end).add(dayOffset, 'days').toISOString(),
      }
    })

    const orphanedIds = moved
      .filter((x) => isShiftOutOfRange(x.start, rangeStart, rangeEnd))
      .map((x) => x._id)

    setShiftsList(moved)
    setSelectedShifts(
      moved.filter((x) => !orphanedIds.includes(x._id)).map((x) => x._id),
    )

    if (orphanedIds.length) {
      setToast({
        message: `${orphanedIds.length} shift${
          orphanedIds.length === 1 ? ' falls' : 's fall'
        } outside the new event dates and ${
          orphanedIds.length === 1 ? 'has' : 'have'
        } been unticked`,
        severity: 'warning',
      })
    }
  }

  const goToShiftsStep = () => {
    if (formRef.current && !formRef.current.reportValidity()) return
    if (dateRangeInvalid) return
    rebaseShiftsToNewDates()
    setStep(1)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Enter on a step-1 field triggers form submission — treat it as Next
    if (step === 0) {
      goToShiftsStep()
      return
    }

    const shiftsToKeep = shiftsList
      .filter((x) => selectedShifts.includes(x._id))
      .map((x) => ({
        start: x.start,
        end: x.end,
        clinicalRole: x.clinicalRole,
        payRate: x.payRate,
        hourlyRate: x.hourlyRate,
        bonuses: (x.bonuses ?? []).map((bonus) => ({
          label: bonus.label,
          amount: bonus.amount,
        })),
        travel: x.travel
          ? {
              beforeMinutes: x.travel.beforeMinutes,
              afterMinutes: x.travel.afterMinutes,
              rate: x.travel.rate,
            }
          : null,
        staffCanBook: x.staffCanBook,
      }))

    API.post('/events/duplicate-event', {
      eventName,
      eventStartDate,
      eventEndDate,
      organisation,
      eventType,
      shifts: shiftsToKeep,
      ...eventAddressPayload(eventAddress),
      eventID: eventMeta._id,
    }).then((res) => {
      if (res.status === 200) {
        navigate(`/events/${res.data.newEventID}`)
        setToast({ message: 'Event successfully duplicated' })
        onClose?.()
      } else {
        setToast({
          message: 'Unable to duplicate event. Please contact support.',
          severity: 'error',
        })
      }
    })
  }

  return (
    <Grid
      container
      rowSpacing={2}
      columnSpacing={1}
      component="form"
      ref={formRef}
      onSubmit={handleSubmit}
    >
      <Grid item xs={12}>
        <Typography variant="title2">Duplicate Event</Typography>
      </Grid>

      <Grid item xs={12}>
        <Stepper activeStep={step}>
          <Step>
            <StepLabel>Event Details</StepLabel>
          </Step>
          <Step>
            <StepLabel>Shifts</StepLabel>
          </Step>
        </Stepper>
      </Grid>

      {step === 0 && (
        <>
          <Grid item xs={12} lg={4}>
            <TextField
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              label="New Event Name"
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <AnDateTimePicker
              date={eventStartDate}
              onChange={setEventStartDate}
              label="Start Date"
              fullWidth
              error={
                !!eventStartDate &&
                !!eventEndDate &&
                moment(eventStartDate).isAfter(eventEndDate)
              }
              required
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <AnDateTimePicker
              date={eventEndDate}
              onChange={setEventEndDate}
              label="End Date"
              fullWidth
              error={
                !!eventStartDate &&
                !!eventEndDate &&
                moment(eventEndDate).isBefore(eventStartDate)
              }
              required
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <FormControl fullWidth>
              <Autocomplete
                options={organisationsOptions}
                id="organisation-select"
                getOptionLabel={(option) => option?.orgName ?? ''}
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id
                }
                value={organisation}
                onChange={(e, value) => setOrganisation(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Organisation" required />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Autocomplete
              options={Object.keys(eventTypes).sort()}
              id="eventType-select"
              value={eventType}
              onChange={(e, value) => setEventType(value ?? '')}
              renderInput={(params) => (
                <TextField {...params} label="Event Type" required />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="title3">New Event Location</Typography>
          </Grid>
          <Grid item xs={12}>
            <EventAddressFields
              value={eventAddress}
              onChange={setEventAddress}
              legacyLocation={
                eventMeta?.eventAddress ? undefined : eventMeta?.eventLocation
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" color="inherit" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={goToShiftsStep}
                disabled={dateRangeInvalid}
              >
                Next: Shifts
              </Button>
            </Stack>
          </Grid>
        </>
      )}

      {step === 1 && (
        <>
          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="title3">Shifts to Duplicate</Typography>

              <Typography variant="caption" color="text.secondary">
                Shifts have been moved to line up with the new event dates,
                keeping their original times and day spacing.
              </Typography>

              {outOfRangeShiftIds.length > 0 && (
                <Typography variant="caption" color="error">
                  {outOfRangeShiftIds.length === 1
                    ? 'A highlighted shift falls'
                    : `${outOfRangeShiftIds.length} highlighted shifts fall`}{' '}
                  outside the new event dates and{' '}
                  {outOfRangeShiftIds.length === 1 ? 'has' : 'have'} been
                  unticked. Adjust the shift dates to include{' '}
                  {outOfRangeShiftIds.length === 1 ? 'it' : 'them'}, or go back
                  to change the event dates.
                </Typography>
              )}

              {shiftsList.map((shift) => (
                <Stack
                  direction="row"
                  gap={0.5}
                  key={shift._id}
                  sx={
                    outOfRangeShiftIds.includes(shift._id)
                      ? {
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          borderRadius: 1,
                        }
                      : undefined
                  }
                >
                  <Checkbox
                    checked={selectedShifts.includes(shift._id)}
                    onChange={(e) =>
                      setSelectedShifts((prev) =>
                        e.target.checked
                          ? prev.includes(shift._id)
                            ? prev
                            : [...prev, shift._id]
                          : prev.filter((x) => x !== shift._id),
                      )
                    }
                  />

                  <AnDateTimePicker
                    label="Start"
                    date={shift.start}
                    onChange={(newIso) =>
                      updateShift(shift._id, { start: newIso })
                    }
                    error={outOfRangeShiftIds.includes(shift._id)}
                    required
                  />

                  <AnDateTimePicker
                    label="End"
                    date={shift.end}
                    onChange={(newIso) => updateShift(shift._id, { end: newIso })}
                    error={outOfRangeShiftIds.includes(shift._id)}
                    required
                  />

                  <Box display="flex" flex={1}>
                    <FormControl fullWidth>
                      <InputLabel id={`role-label-${shift._id}`}>
                        Role
                      </InputLabel>
                      <Select
                        labelId={`role-label-${shift._id}`}
                        label="Role"
                        value={shift.clinicalRole ?? ''}
                        onChange={(e) =>
                          updateShift(shift._id, {
                            clinicalRole: e.target.value,
                          })
                        }
                        required
                      >
                        {(orgConfig?.orgSettings?.roles ?? []).map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    paddingX={1}
                  >
                    <Switch
                      checked={!!shift.staffCanBook}
                      onChange={(e) =>
                        updateShift(shift._id, {
                          staffCanBook: e.target.checked,
                        })
                      }
                    />
                    <Typography variant="caption">Bookable</Typography>
                  </Stack>

                  <TextField
                    value={shift.payRate ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value
                      updateShift(shift._id, {
                        payRate: raw === '' ? '' : Number(raw),
                      })
                    }}
                    label="Rate"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {orgConfig?.orgSettings?.currency ?? '£'}
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {shift.hourlyRate ? 'per hour' : 'per shift'}
                        </InputAdornment>
                      ),
                    }}
                    required
                  />

                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    paddingX={1}
                  >
                    <Typography variant="caption">Fixed Rate</Typography>
                    <Switch
                      checked={!!shift.hourlyRate}
                      onChange={(e) =>
                        updateShift(shift._id, { hourlyRate: e.target.checked })
                      }
                    />
                    <Typography variant="caption">Hourly Rate</Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Stack direction="row" gap={1}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setStep(0)}
                >
                  Back
                </Button>
                <Button variant="outlined" color="inherit" onClick={onClose}>
                  Cancel
                </Button>
              </Stack>
              <Button
                variant="contained"
                color="success"
                type="submit"
                disabled={dateRangeInvalid}
              >
                Duplicate Event
              </Button>
            </Stack>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default DuplicateEvent
